/* eslint-disable i18next/no-literal-string */
import mapboxgl, {
  Layer,
  Map,
  MapboxGeoJSONFeature,
  MapMouseEvent,
  Popup,
} from "mapbox-gl";
import {
  DataTableTooltipContent,
  idForLayer,
  isSeaSketchLayerId,
  layerIdFromStyleLayerId,
  Tooltip,
} from "./MapContextManager";
import Mustache from "mustache";
import {
  BasemapDetailsFragment,
  DataLayerDetailsFragment,
  DataSourceDetailsFragment,
  DataSourceTypes,
  InteractivityType,
} from "../generated/graphql";
import { EventEmitter } from "eventemitter3";
import { CustomGLSource } from "@seasketch/mapbox-gl-esri-sources";
import { identifyLayers } from "../admin/data/arcgis/arcgis";
import {
  fetchInaturalistUtfgrid,
  renderInaturalistPopup,
} from "./inaturalistInteractivity";
import { normalizeInaturalistParams } from "./inaturalist";
import {
  encodingParamsFromGlStyles,
  styleRespectsScaleAndOffset,
  type RasterValueEncodingParams,
} from "./rasterValueEncoding";
import {
  clearRasterPixelQueryCaches,
  queryRasterPixelValue,
  setRasterPixelQueryAuthorizeUrl,
  type RasterPixelHit,
} from "./rasterPixelQuery";
import {
  extractRasterLabelFormatOptions,
  extractRasterLegendLabels,
  rasterInteractionLabel,
} from "./rasterLegendLabel";
import {
  isBlankHoverMessage,
  rasterPaintStyleFromGlStyles,
  rasterValueIsVisible,
} from "./rasterPaintVisibility";
import { decodeDataTableRawValue } from "./dataTableMapStyle";
import { formatLegendNumber } from "./legends/DataTableLegendBubble";

const PopupNumberFormatter = Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
});

type InteractiveRasterLayer = {
  layer: DataLayerDetailsFragment;
  source: DataSourceDetailsFragment;
  encoding: RasterValueEncodingParams;
  /** Pre-extracted from mapboxGlStyles; avoid re-parsing on mousemove. */
  legendLabels: Record<string, string>;
  /** Continuous legend presentation: round-numbers + value-suffix. */
  labelFormat: ReturnType<typeof extractRasterLabelFormatOptions>;
  respectScaleAndOffset: boolean;
  /** Null when the style has no raster-color (every opaque sample is visible). */
  paint: ReturnType<typeof rasterPaintStyleFromGlStyles>;
  glLayerId: string;
};

export type InteractivityUIUpdate = Partial<{
  bannerMessages: string[];
  tooltip: Tooltip | undefined;
  fixedBlocks: string[];
  sidebarPopupContent: string | undefined;
  sidebarPopupTitle: string | undefined;
  /**
   * Mount point for the Data Tables call-to-action inside the currently open
   * map popup. React portals DataTablesPopupFooter into `element`.
   */
  dataTablesPopupTarget: DataTablesPopupFooterTarget | undefined;
  /** TOC stableId of the layer shown in the sidebar popup, if any. */
  sidebarPopupTocStableId: string | undefined;
}>;

/** Element within an open map popup that hosts the Data Tables footer CTA. */
export type DataTablesPopupFooterTarget = {
  element: HTMLElement;
  tocStableId: string;
  /**
   * Properties of the clicked vector feature, so data-table UI (e.g. the
   * "Show rows in calculation" modal) can preselect the site the user
   * clicked via the table's overlay join column.
   */
  featureProperties?: { [name: string]: any };
};

/** Active data-table proportional-symbol layer for value tooltips. */
export type DataTableInteractiveLayer = {
  /** Mapbox GL style layer id (`idForLayer(layer, 0)`). */
  glLayerId: string;
  sourceId: string;
  sourceLayer?: string;
  column: string;
  op: string;
  columnLabel?: string;
  tableName?: string;
  layerTitle?: string;
  getSeries?: (featureId: string) => {
    points: DataTableTooltipContent["series"];
    currentSteps: string[];
  } | null;
  getRangeLabel?: () => string | undefined;
};

/**
 * LayerInteractivityManager works in tandem with the MapContextManager to react
 * to user interaction which clicks, hovers, or otherwise interacts with map content
 * to show popups and other extra information. It operates on both vector layers and
 * image layers for which it is necessary to call an external service to retrieve
 * attribute information.
 */
export default class LayerInteractivityManager extends EventEmitter {
  private map: Map;
  private _onUIUpdate: (update: InteractivityUIUpdate) => void;
  private _previousUIState: InteractivityUIUpdate = {};

  /** List of interactive layers that are currently shown on the map. Update with setVisibleLayers() */
  private layers: { [layerId: string]: DataLayerDetailsFragment } = {};
  /**
   * List of image sources related to visible layers. Note only sources that
   * require api requests to display popups need to be stored
   */
  private imageSources: { [sourceId: string]: DataSourceDetailsFragment } = {};
  private previousInteractionTarget?: string;
  private debouncedMouseMoveListenerReference?: any;
  private popupAbortController: AbortController | undefined;
  private interactiveVectorLayerIds: string[] = [];
  private interactiveImageLayerIds: string[] = [];
  private interactiveRasterLayers: InteractiveRasterLayer[] = [];
  /** glLayerId → data-table value tooltip config (independent of admin interactivity). */
  private dataTableLayersByGlId: {
    [glLayerId: string]: DataTableInteractiveLayer;
  } = {};
  private basemap: BasemapDetailsFragment | undefined;
  private sketchLayerIds: string[] = [];
  private focusedSketchId?: number;
  private inaturalistConfigs: {
    params: ReturnType<typeof normalizeInaturalistParams>;
    sourceId: string;
    layerLabel?: string;
  }[] = [];
  /** The single open Mapbox popup, if any (vector, image, or iNaturalist). */
  private activePopup?: Popup;
  private customSources: { [sourceId: string]: CustomGLSource<any> } = {};
  private tocItemLabels: {
    [stableId: string]: { label?: string; dataTableCount?: number };
  } = {};
  private selectedFeature?: mapboxgl.FeatureIdentifier;
  private hoveredFeature?: mapboxgl.FeatureIdentifier;
  /** Bumps to drop stale async raster hover results. */
  private rasterHoverGeneration = 0;
  /** TOC stableIds paint-hidden by the map clock. */
  private clockHiddenTocIds = new Set<string>();

  /**
   *
   * @param map Map that should support user interaction. Use #setMap to update
   * @param onUIUpdate Callback when UI state (tooltip, banner, popup) changes.
   */
  constructor(map: Map, onUIUpdate: (update: InteractivityUIUpdate) => void) {
    super();
    this.map = map;
    this.registerEventListeners(map);
    this._onUIUpdate = onUIUpdate;
  }

  /**
   * Update the map that supports user interaction. Will update all event listeners
   * @param map
   */
  setMap(map: Map) {
    if (this.map) {
      this.unregisterEventListeners(this.map);
    }
    this.map = map;
    this.registerEventListeners(map);
  }

  setCustomSources(sources: { [sourceId: string]: CustomGLSource<any> }) {
    this.customSources = sources;
  }

  /**
   * Overlay TOC items currently paint-hidden by the timeslider. Raster pixel
   * queries ignore Mapbox opacity, so this list (and paint-opacity === 0)
   * must be consulted on hover — not only `setVisibleLayers`.
   */
  setClockHiddenTocIds(ids: string[]) {
    this.clockHiddenTocIds = new Set(ids);
  }

  private tocIdOfLayer(layer: DataLayerDetailsFragment): string | undefined {
    const withToc = layer as DataLayerDetailsFragment & { tocId?: string };
    return withToc.tocId;
  }

  private isClockHiddenLayer(layer: DataLayerDetailsFragment): boolean {
    const tocId = this.tocIdOfLayer(layer);
    return Boolean(tocId && this.clockHiddenTocIds.has(tocId));
  }

  private isClockHiddenGlLayer(glLayerId: string): boolean {
    const dataLayer = isSeaSketchLayerId(glLayerId)
      ? this.layers[layerIdFromStyleLayerId(glLayerId)]
      : this.layers[glLayerId];
    return Boolean(dataLayer && this.isClockHiddenLayer(dataLayer));
  }

  /**
   * Clock hide sets a constant opacity of 0. Mapbox still returns those
   * features from queryRenderedFeatures, and raster hits read the source
   * directly.
   */
  private isPaintHiddenOnMap(
    glLayerId: string,
    layer = this.map.getLayer(glLayerId)
  ): boolean {
    if (!layer) return true;
    const type = layer && "type" in layer ? layer.type : undefined;
    let prop: string | undefined;
    if (type === "raster") prop = "raster-opacity";
    else if (type === "fill") prop = "fill-opacity";
    else if (type === "line") prop = "line-opacity";
    else if (type === "circle") prop = "circle-opacity";
    else if (type === "symbol") prop = "icon-opacity";
    else if (type === "heatmap") prop = "heatmap-opacity";
    else if (type === "fill-extrusion") prop = "fill-extrusion-opacity";
    if (!prop) return false;
    try {
      return this.map.getPaintProperty(glLayerId, prop) === 0;
    } catch {
      return false;
    }
  }

  setSketchLayerIds(ids: string[]) {
    this.sketchLayerIds = ids;
    this.syncMouseMoveListener();
  }

  setFocusedSketchId(id: number | null) {
    this.focusedSketchId = id || undefined;
  }

  /**
   * Layers with an active data-table visualization. Hover shows a value
   * tooltip from `rawValue` feature-state. Works alongside popup / sidebar /
   * banner interactivity; overrides an admin Tooltip (and SidebarOverlay
   * short-template hover tooltip) because both compete for the same UI slot.
   */
  setDataTableLayers(layers: DataTableInteractiveLayer[]) {
    const next: { [glLayerId: string]: DataTableInteractiveLayer } = {};
    for (const layer of layers) {
      next[layer.glLayerId] = layer;
    }
    this.dataTableLayersByGlId = next;
    this.syncMouseMoveListener();
  }

  /** Authorize hosted tile fetches (wired from MapContextManager). */
  setAuthorizeUrl(fn: (url: string) => string) {
    setRasterPixelQueryAuthorizeUrl(fn);
  }

  private syncMouseMoveListener() {
    this.map.off("mousemove", this.debouncedMouseMoveListener);
    if (
      this.interactiveVectorLayerIds.length > 0 ||
      this.interactiveRasterLayers.length > 0 ||
      this.sketchLayerIds.length > 0 ||
      this.inaturalistConfigs.length > 0 ||
      Object.keys(this.dataTableLayersByGlId).length > 0
    ) {
      this.map.on("mousemove", this.debouncedMouseMoveListener);
    }
  }

  /** Layer ids that currently exist on the map (queryRenderedFeatures throws otherwise). */
  private existingLayerIds(ids: string[]) {
    return ids.filter((id) => {
      const layer = this.map.getLayer(id);
      if (!layer || this.isClockHiddenGlLayer(id)) return false;
      // Data-table layers may not have admin interactivity and therefore may
      // not be represented in `this.layers`; retain the paint fallback only
      // for that case instead of querying Mapbox paint for every hover layer.
      return (
        !this.dataTableLayersByGlId[id] ||
        !this.isPaintHiddenOnMap(id, layer)
      );
    });
  }

  /**
   * Should be called by the MapContextManager whenever visible layers change so
   * that this module can update it's internal list of visible interactive layers.
   * @param dataLayers
   */
  async setVisibleLayers(
    dataLayers: DataLayerDetailsFragment[],
    dataSources: { [dataSourceId: string]: DataSourceDetailsFragment },
    basemap: BasemapDetailsFragment,
    tocItemLabels: {
      [stableId: string]: {
        label?: string;
        dataTableCount?: number;
      };
    }
  ) {
    this.tocItemLabels = tocItemLabels;
    const newActiveLayers: { [layerId: string]: DataLayerDetailsFragment } = {};
    const newActiveImageSources: {
      [sourceId: string]: DataSourceDetailsFragment;
    } = {};
    const newInteractiveImageLayerIds: string[] = [];
    let newInteractiveVectorLayerIds: string[] = [];
    const newInteractiveRasterLayers: InteractiveRasterLayer[] = [];
    const newInaturalistConfigs: {
      params: ReturnType<typeof normalizeInaturalistParams>;
      sourceId: string;
      layerLabel?: string;
    }[] = [];
    for (const layer of dataLayers) {
      if (layer && layer.dataSourceId) {
        const source = dataSources[layer.dataSourceId];
        if (source) {
          if (source.type === DataSourceTypes.Inaturalist) {
            newInaturalistConfigs.push({
              params: normalizeInaturalistParams(
                (source.queryParameters as any) || {}
              ),
              sourceId: source.id.toString(),
              layerLabel:
                (this.tocItemLabels || {})[
                  (layer as any).tocId || layer.id.toString()
                ]?.label ||
                (layer as any).tocId ||
                layer.id.toString(),
            });
          }
          if (
            layer.interactivitySettings &&
            layer.interactivitySettings.type !== InteractivityType.None
          ) {
            if (layer.sublayer) {
              newInteractiveImageLayerIds.push(layer.id.toString());
              newActiveImageSources[source.id] = source;
              newActiveLayers[layer.id] = layer;
            } else if (source.type === DataSourceTypes.SeasketchRaster) {
              // Only Banner/Tooltip/Popup are implemented for rasters. Legacy
              // Sidebar/AllProperties/etc. must not register or they win
              // z-order and swallow hover for layers underneath.
              const rasterType = layer.interactivitySettings.type;
              const encoding = encodingParamsFromGlStyles(
                layer.mapboxGlStyles
              );
              if (
                encoding &&
                source.url &&
                (rasterType === InteractivityType.Banner ||
                  rasterType === InteractivityType.Tooltip ||
                  rasterType === InteractivityType.Popup)
              ) {
                newInteractiveRasterLayers.push({
                  layer,
                  source,
                  encoding,
                  legendLabels: extractRasterLegendLabels(
                    layer.mapboxGlStyles
                  ),
                  labelFormat: extractRasterLabelFormatOptions(
                    layer.mapboxGlStyles
                  ),
                  respectScaleAndOffset: styleRespectsScaleAndOffset(
                    layer.mapboxGlStyles
                  ),
                  paint: rasterPaintStyleFromGlStyles(layer.mapboxGlStyles),
                  glLayerId: idForLayer(layer, 0),
                });
                newActiveLayers[layer.id] = layer;
              }
            } else {
              let GLStyles: Layer[];
              if (layer.mapboxGlStyles && Array.isArray(layer.mapboxGlStyles)) {
                GLStyles = layer.mapboxGlStyles;
                newInteractiveVectorLayerIds = [
                  ...newInteractiveVectorLayerIds,
                  ...GLStyles.map((s, i) => idForLayer(layer, i)),
                ];
                newActiveLayers[layer.id] = layer;
              } else if (source.type === DataSourceTypes.ArcgisVector) {
                const customSource = this.customSources[source.id];
                if (customSource) {
                  const { layers } = await customSource.getGLStyleLayers();
                  newInteractiveVectorLayerIds = [
                    ...newInteractiveVectorLayerIds,
                    ...layers.map((l) => l.id),
                  ];
                  for (const lyr of layers) {
                    newActiveLayers[lyr.id] = layer;
                  }
                  // newActiveLayers[layer.id] = layer;
                }
              }
            }
          }
        }
      }
    }
    if (
      basemap &&
      basemap.interactivitySettings &&
      basemap.interactivitySettings.type !== InteractivityType.None &&
      basemap.interactivitySettings.layers?.length
    ) {
      newInteractiveVectorLayerIds = [
        ...newInteractiveVectorLayerIds,
        ...(basemap.interactivitySettings.layers as string[]),
      ];
    } else {
      // delete this.basemap;
    }
    this.basemap = basemap;
    // Only one popup can be open; dismiss it whenever the interactive set changes
    // (including turning a layer off).
    if (
      !sameStringSet(this.interactiveVectorLayerIds, newInteractiveVectorLayerIds) ||
      !sameStringSet(this.interactiveImageLayerIds, newInteractiveImageLayerIds) ||
      !sameStringSet(
        this.interactiveRasterLayers.map((r) => r.glLayerId),
        newInteractiveRasterLayers.map((r) => r.glLayerId)
      ) ||
      !sameStringSet(
        this.inaturalistConfigs.map((c) => c.sourceId),
        newInaturalistConfigs.map((c) => c.sourceId)
      )
    ) {
      this.dismissOpenPopup();
    }
    this.interactiveImageLayerIds = newInteractiveImageLayerIds;
    this.interactiveVectorLayerIds = newInteractiveVectorLayerIds;
    this.interactiveRasterLayers = newInteractiveRasterLayers;
    this.inaturalistConfigs = newInaturalistConfigs;
    this.layers = newActiveLayers;
    this.imageSources = newActiveImageSources;
    this.syncMouseMoveListener();
  }

  private setActivePopup(popup: Popup, footerTarget?: DataTablesPopupFooterTarget) {
    this.activePopup?.remove();
    this.activePopup = popup;
    this.updateUI({ dataTablesPopupTarget: footerTarget });
    popup.once("close", () => {
      if (this.activePopup === popup) {
        this.activePopup = undefined;
        this.updateUI({ dataTablesPopupTarget: undefined });
      }
    });
  }

  /**
   * Close the open map popup, if any. Used by the Data Tables popup footer
   * after a table is activated so the new visualization isn't obscured.
   */
  closeActivePopup() {
    this.activePopup?.remove();
    this.activePopup = undefined;
  }

  /** Resolve the TOC stableId of the overlay layer that produced a feature. */
  private tocIdForFeature(
    feature: MapboxGeoJSONFeature
  ): string | undefined {
    const dataLayer = isSeaSketchLayerId(feature.layer.id)
      ? this.layers[layerIdFromStyleLayerId(feature.layer.id)]
      : this.layers[feature.layer.id];
    return dataLayer ? this.tocIdOfLayer(dataLayer) : undefined;
  }

  /**
   * Wraps rendered popup HTML in a container and, if the source TOC item has
   * data tables, appends an empty footer element. React (MapboxMap) portals
   * the DataTablesPopupFooter call-to-action into that element, keeping the
   * footer consistent across custom templates and all-columns popups.
   */
  private popupContentWithDataTablesFooter(
    html: string,
    tocStableId?: string,
    feature?: MapboxGeoJSONFeature
  ): {
    element: HTMLElement;
    footerTarget?: DataTablesPopupFooterTarget;
  } {
    const container = document.createElement("div");
    container.innerHTML = html;
    if (
      tocStableId &&
      (this.tocItemLabels[tocStableId]?.dataTableCount || 0) > 0
    ) {
      const footer = document.createElement("div");
      footer.className = "seasketch-data-tables-popup-footer";
      container.appendChild(footer);
      return {
        element: container,
        footerTarget: {
          element: footer,
          tocStableId,
          featureProperties: feature?.properties || undefined,
        },
      };
    }
    return { element: container };
  }

  private dismissOpenPopup() {
    this.activePopup?.remove();
    this.activePopup = undefined;
    this.clearSidebarPopup();
    if (this.popupAbortController) {
      this.popupAbortController.abort();
      delete this.popupAbortController;
    }
  }

  /**
   * Call to remove all event listeners from a map.
   */
  destroy() {
    this.unregisterEventListeners(this.map);
    clearRasterPixelQueryCaches();
    this.rasterHoverGeneration += 1;
  }

  private unregisterEventListeners(map: Map) {
    map.off("mouseout", this.onMouseOut);
    map.off("mousemove", this.debouncedMouseMoveListener);
    map.off("click", this.onMouseClick);
    map.off("movestart", this.onMoveStart);
    map.off("moveend", this.onMoveEnd);
  }

  private registerEventListeners(map: Map) {
    map.on("mouseout", this.onMouseOut);
    map.on("click", this.onMouseClick);
    map.on("movestart", this.onMoveStart);
    map.on("moveend", this.onMoveEnd);
  }

  private paused = false;

  /**
   * Pause interactivity manager so that it doesn't interfere with other map
   * interactions, such as when using the measure tool.
   */
  pause() {
    this.paused = true;
  }

  /**
   * Resume interactivity manager after pausing it.
   */
  resume() {
    this.paused = false;
  }

  private moving = false;
  private lastMouseEvent: MapMouseEvent | undefined = undefined;

  private updateUI(update: InteractivityUIUpdate) {
    const next = { ...this._previousUIState, ...update };
    const prev = this._previousUIState;
    if (
      prev.bannerMessages?.join("") !== next.bannerMessages?.join("") ||
      prev.fixedBlocks?.join("") !== next.fixedBlocks?.join("") ||
      prev.tooltip !== next.tooltip ||
      prev.sidebarPopupContent !== next.sidebarPopupContent ||
      prev.sidebarPopupTitle !== next.sidebarPopupTitle ||
      prev.sidebarPopupTocStableId !== next.sidebarPopupTocStableId ||
      prev.dataTablesPopupTarget !== next.dataTablesPopupTarget
    ) {
      this._previousUIState = next;
      this._onUIUpdate(update);
    }
  }

  private onMoveStart = () => {
    if (this.paused) {
      return;
    }
    this.rasterHoverGeneration += 1;
    this.updateUI({
      bannerMessages: [],
      fixedBlocks: [],
      tooltip: undefined,
    });
    delete this.previousInteractionTarget;
    this.moving = true;
  };

  private onMoveEnd = () => {
    if (this.paused) {
      return;
    }
    this.moving = false;
    if (this.lastMouseEvent) {
      void this.mouseMoveListener(this.lastMouseEvent);
    }
  };

  private onMouseOut = () => {
    if (this.paused) {
      return;
    }
    this.rasterHoverGeneration += 1;
    if (this.hoveredFeature) {
      this.map.setFeatureState(this.hoveredFeature, {
        hovered: false,
        selected:
          this.selectedFeature &&
          this.selectedFeature?.id === this.hoveredFeature?.id &&
          this.selectedFeature?.source === this.hoveredFeature?.source &&
          this.selectedFeature?.sourceLayer ===
            this.hoveredFeature?.sourceLayer,
      });
    }
    setTimeout(() => {
      delete this.previousInteractionTarget;
      this.updateUI({
        tooltip: undefined,
        bannerMessages: [],
        fixedBlocks: [],
      });
    }, 7);
  };

  private onMouseClick = async (e: MapMouseEvent) => {
    if (this.paused) {
      return;
    }
    if (this.popupAbortController) {
      this.popupAbortController.abort();
      delete this.popupAbortController;
    }
    const sketchLayerIds = this.existingLayerIds(this.sketchLayerIds || []);
    const vectorLayerIds = this.existingLayerIds(
      this.interactiveVectorLayerIds
    );
    const sketchFeatures = sketchLayerIds.length
      ? this.map!.queryRenderedFeatures(e.point, {
          layers: sketchLayerIds,
        })
      : [];
    const features = vectorLayerIds.length
      ? this.map!.queryRenderedFeatures(e.point, {
          layers: vectorLayerIds,
        })
      : [];
    const allFeatures = [...sketchFeatures, ...features];
    sortFeaturesByLayer(allFeatures, this.map.getStyle());
    const top = allFeatures[0];
    if (sketchFeatures.includes(top)) {
      const feature = top;
      if (this.focusedSketchId) {
        const id = feature.id?.toString();
        if (id) {
          if (this.focusedSketchId === parseInt(id)) {
            this.emit("click:focused-sketch", feature, e);
          }
          e.preventDefault();
          return;
        } else {
          throw new Error("Sketch display GeoJSON does not have ID assigned.");
        }
      } else {
        this.emit("click:sketch", feature, e);
        e.preventDefault();
        return;
      }
    }
    let vectorPopupOpened = false;
    const inatHit = await this.getTopInaturalistHit(e);
    const inatDrawIndex = inatHit ? inatHit.layerIndex : -Infinity;
    const rasterHit = await this.getTopRasterHit(e);
    const rasterIndex = rasterHit?.layerIndex ?? -Infinity;
    const topVectorIndex = top
      ? this.getLayerDrawIndex(top.layer.id)
      : -Infinity;

    if (
      rasterHit &&
      rasterIndex > topVectorIndex &&
      rasterIndex >= inatDrawIndex
    ) {
      const interactivitySetting =
        rasterHit.layer.layer.interactivitySettings;
      if (
        interactivitySetting &&
        interactivitySetting.type === InteractivityType.Popup
      ) {
        const content = Mustache.render(
          interactivitySetting.longTemplate || "{{label}}",
          this.rasterMustacheContext(rasterHit.hit, rasterHit.layer)
        );
        // Template rendered no readable text. Don't open an empty popup;
        // let a vector feature underneath handle the click.
        if (!isBlankHoverMessage(content)) {
          this.clearSidebarPopup();
          const { element, footerTarget } =
            this.popupContentWithDataTablesFooter(
              content,
              this.tocIdOfLayer(rasterHit.layer.layer)
            );
          this.setActivePopup(
            new Popup({ closeOnClick: true, closeButton: true })
              .setLngLat([e.lngLat.lng, e.lngLat.lat])
              .setDOMContent(element)
              .addTo(this.map!),
            footerTarget
          );
          return;
        }
      }
    }

    if (top) {
      if (inatHit && inatDrawIndex >= topVectorIndex) {
        this.clearSidebarPopup();
        this.setActivePopup(
          await renderInaturalistPopup(this.map, inatHit.hit!)
        );
        return;
      }
      const interactivitySetting = this.getInteractivitySettingForFeature(top);
      if (
        interactivitySetting &&
        (interactivitySetting.type === InteractivityType.Popup ||
          interactivitySetting.type === InteractivityType.SidebarOverlay)
      ) {
        vectorPopupOpened = true;
        const props = top.properties;
        // Special handling for esriAttachments
        if (
          props &&
          "esriAttachments" in props &&
          typeof props.esriAttachments === "string"
        ) {
          try {
            props.esriAttachments = JSON.parse(props.esriAttachments);
          } catch (e) {
            // do nothing
          }
        }
        const content = Mustache.render(
          interactivitySetting.longTemplate || "",
          {
            ...mustacheHelpers,
            ...props,
          }
        );
        if (interactivitySetting.type === InteractivityType.Popup) {
          this.clearSidebarPopup();
          const { element, footerTarget } =
            this.popupContentWithDataTablesFooter(
              content,
              this.tocIdForFeature(top),
              top
            );
          this.setActivePopup(
            new Popup({ closeOnClick: true, closeButton: true })
              .setLngLat([e.lngLat.lng, e.lngLat.lat])
              .setDOMContent(element)
              .addTo(this.map!),
            footerTarget
          );
        } else {
          this.activePopup?.remove();
          this.activePopup = undefined;
          const titleContent = Mustache.render(
            interactivitySetting.title || "",
            {
              ...mustacheHelpers,
              ...top.properties,
            }
          );
          this.updateUI({
            sidebarPopupContent: content,
            sidebarPopupTitle: titleContent,
            sidebarPopupTocStableId: this.tocIdForFeature(top),
          });
          this.setSelectedFeature(top);
        }
      } else if (
        interactivitySetting &&
        interactivitySetting.type === InteractivityType.AllPropertiesPopup
      ) {
        this.clearSidebarPopup();
        const lyr = this.layers[top.layer.id];
        // @ts-ignore
        const layerLabel = (this.tocItemLabels || {})[lyr?.tocId]?.label;
        const allPropsContent = Mustache.render(
                `
              <div class="">
              ${
                layerLabel
                  ? `<h3 class="font-medium p-1">${layerLabel}</h3>`
                  : ""
              }
              <div class="zebra-stripe-child-div max-h-72 overflow-y-auto">
              ${Object.keys(top.properties || {})
                .map((key) => {
                  const value = (top.properties || {})[key];
                  const isNumber = typeof value === "number";
                  const strValue = isNumber
                    ? PopupNumberFormatter.format(value)
                    : value.toString();
                  return `
                      <div class="flex space-x-3 px-1 py-0.5">
                        <div class="flex-1 truncate">${key}</div>
                        <div class="truncate ${
                          isNumber ? "font-mono" : ""
                        }">${strValue}
                        </div>
                      </div>`;
                })
                .join("\n")}
                </div>
              </div>
            `,
          {
            ...mustacheHelpers,
            properties: top.properties,
          }
        );
        const { element, footerTarget } = this.popupContentWithDataTablesFooter(
          allPropsContent,
          this.tocIdForFeature(top),
          top
        );
        this.setActivePopup(
          new Popup({ closeOnClick: true, closeButton: true })
            .setLngLat([e.lngLat.lng, e.lngLat.lat])
            .setDOMContent(element)
            .addTo(this.map!),
          footerTarget
        );
        vectorPopupOpened = true;
      }
    } else {
      this.clearSidebarPopup();
    }
    let popupOpened = vectorPopupOpened;

    if (!popupOpened && this.inaturalistConfigs.length) {
      if (inatHit?.hit) {
        this.clearSidebarPopup();
        this.setActivePopup(
          await renderInaturalistPopup(this.map, inatHit.hit)
        );
        popupOpened = true;
      }
    }

    if (!popupOpened) {
      // Are any image layers active that support identify tools?
      const interactiveImageLayers = this.interactiveImageLayerIds.map(
        (id) => this.layers[id]
      );
      interactiveImageLayers.sort((a, b) => a.zIndex - b.zIndex);
      if (interactiveImageLayers.length) {
        this.openImageServicePopups(
          [e.lngLat.lng, e.lngLat.lat],
          interactiveImageLayers
        );
      }
    }
  };

  setSelectedFeature(feature?: mapboxgl.FeatureIdentifier) {
    if (this.selectedFeature?.id === feature?.id) {
      return;
    }
    if (this.selectedFeature) {
      this.map.setFeatureState(this.selectedFeature, {
        selected: false,
        hovered:
          this.hoveredFeature &&
          this.hoveredFeature?.id === this.selectedFeature?.id &&
          this.hoveredFeature?.source === this.selectedFeature?.source &&
          this.hoveredFeature?.sourceLayer ===
            this.selectedFeature?.sourceLayer,
      });
    }
    if (feature) {
      this.map.setFeatureState(feature, {
        selected: true,
        hovered:
          this.hoveredFeature &&
          this.hoveredFeature?.id === feature?.id &&
          this.hoveredFeature?.source === feature?.source &&
          this.hoveredFeature?.sourceLayer === feature?.sourceLayer,
      });
    }
    this.selectedFeature = feature;
  }

  setHoveredFeature(feature?: mapboxgl.FeatureIdentifier) {
    if (this.hoveredFeature?.id === feature?.id) {
      return;
    }
    if (this.hoveredFeature) {
      this.map.setFeatureState(this.hoveredFeature, {
        hovered: false,
        selected:
          this.selectedFeature &&
          this.selectedFeature?.id === this.hoveredFeature?.id &&
          this.selectedFeature?.source === this.hoveredFeature?.source &&
          this.selectedFeature?.sourceLayer ===
            this.hoveredFeature?.sourceLayer,
      });
    }
    if (feature) {
      this.map.setFeatureState(feature, {
        hovered: true,
        selected:
          this.selectedFeature &&
          this.selectedFeature?.id === feature?.id &&
          this.selectedFeature?.source === feature?.source &&
          this.selectedFeature?.sourceLayer === feature?.sourceLayer,
      });
    }
    this.hoveredFeature = feature;
  }

  clearSidebarPopup = () => {
    this.updateUI({
      sidebarPopupContent: undefined,
      sidebarPopupTitle: undefined,
      sidebarPopupTocStableId: undefined,
    });
    this.setSelectedFeature(undefined);
  };

  // Note, this will only work with ArcGIS Server
  // TODO: refactor this at some point to support CustomGLSources
  // more generally
  private async openImageServicePopups(
    position: [number, number],
    layers: DataLayerDetailsFragment[]
  ) {
    if (this.popupAbortController) {
      this.popupAbortController.abort();
      delete this.popupAbortController;
    }
    this.popupAbortController = new AbortController();
    const requests: {
      sublayers: string[];
      source: DataSourceDetailsFragment;
    }[] = [];
    for (const layer of layers) {
      let existingRequest = requests.find(
        (r) => r.source.id === layer.dataSourceId
      );
      if (!existingRequest) {
        const source = this.imageSources[layer.dataSourceId];
        if (!source) {
          /* eslint-disable-next-line */
          throw new Error(`Could not find source id=${layer.dataSourceId}`);
        }
        existingRequest = {
          sublayers: [],
          source: source,
        };
        requests.push(existingRequest);
      }
      existingRequest.sublayers.push(layer.sublayer!);
    }
    const bounds = this.map!.getBounds();
    const extent = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ] as [number, number, number, number];
    const width = this.map!.getCanvas().width;
    const height = this.map!.getCanvas().height;
    const dpi = window.devicePixelRatio * 96;
    this.map!.getCanvas().style.cursor = "progress";
    const data = await Promise.all(
      requests.map((request) => {
        return identifyLayers(
          position,
          request.source,
          request.sublayers,
          extent,
          width,
          height,
          dpi,
          this.popupAbortController
        );
      })
    );
    this.map!.getCanvas().style.cursor = "";
    if (!this.popupAbortController.signal.aborted) {
      for (const sublayerData of data) {
        if (sublayerData.length) {
          const interactivitySetting = layers.find(
            (l) =>
              l.sublayer?.toString() === sublayerData[0].sublayer.toString() &&
              l.dataSourceId === sublayerData[0].sourceId
          )?.interactivitySettings;
          if (
            interactivitySetting?.type === InteractivityType.AllPropertiesPopup
          ) {
            const data = sublayerData[0];
            let layerLabel: string | undefined;
            if (data) {
              const lyr = layers.find(
                (l) =>
                  l.sublayer?.toString() === data.sublayer.toString() &&
                  l.dataSourceId === data.sourceId
              );
              // @ts-ignore
              layerLabel = (this.tocItemLabels || {})[lyr?.tocId]?.label;
            }

            const properties = sublayerData[0]?.attributes || {};
            this.setActivePopup(
              new Popup({ closeOnClick: true, closeButton: true })
                .setLngLat(position)
                .setHTML(
                  Mustache.render(
                    `
                  ${
                    layerLabel
                      ? `<h3 class="font-medium p-1">${layerLabel}</h3>`
                      : ""
                  }    
              <div class="">
              <div class="zebra-stripe-child-div max-h-72 overflow-y-auto">
              ${Object.keys(properties)
                .map((key) => {
                  const value = properties[key];
                  const isNumber = typeof value === "number";
                  const strValue = isNumber
                    ? PopupNumberFormatter.format(value)
                    : value.toString();
                  return `
                      <div class="flex space-x-3 px-1 py-0.5">
                        <div class="flex-1 truncate">${key}</div>
                        <div class="truncate ${
                          isNumber ? "font-mono" : ""
                        }">${strValue}
                        </div>
                      </div>`;
                })
                .join("\n")}
                </div>
              </div>
            `,
                    {
                      ...mustacheHelpers,
                      properties,
                    }
                  )
                )
                .addTo(this.map!)
            );
          } else {
            this.setActivePopup(
              new Popup({ closeOnClick: true, closeButton: true })
                .setLngLat(position)
                .setHTML(
                  Mustache.render(interactivitySetting!.longTemplate || "", {
                    ...mustacheHelpers,
                    ...sublayerData[0].attributes,
                  })
                )
                .addTo(this.map!)
            );
          }
          break;
        }
      }
    }
  }

  private debouncedMouseMoveListener = (e: MapMouseEvent, backoff = 4) => {
    if (this.paused) {
      return;
    }
    if (this.moving) {
      this.lastMouseEvent = e;
      return;
    }
    if (this.debouncedMouseMoveListenerReference) {
      clearTimeout(this.debouncedMouseMoveListenerReference);
    }
    this.debouncedMouseMoveListenerReference = setTimeout(() => {
      delete this.debouncedMouseMoveListenerReference;
      if (this.moving) {
        return;
      }
      void this.mouseMoveListener(e);
    }, backoff);
  };

  private mouseMoveListener = async (e: MapMouseEvent) => {
    if (this.moving) {
      return;
    }
    const clear = () => {
      this.map!.getCanvas().style.cursor = "";
      this.updateUI({
        bannerMessages: [],
        tooltip: undefined,
        fixedBlocks: [],
      });
      delete this.previousInteractionTarget;
    };
    const inatHit = await this.getTopInaturalistHit(e);
    const inatDrawIndex = inatHit ? inatHit.layerIndex : -Infinity;

    // First, check sketch layers
    const sketchLayerIds = this.existingLayerIds(this.sketchLayerIds);
    const sketchFeatures = sketchLayerIds.length
      ? this.map!.queryRenderedFeatures(e.point, {
          layers: sketchLayerIds,
        })
      : [];
    if (sketchFeatures.length) {
      this.setHoveredFeature(undefined);
      clear();
      if (
        !this.focusedSketchId ||
        sketchFeatures[0].id === this.focusedSketchId
      ) {
        this.map!.getCanvas().style.cursor = "pointer";
      }
      return;
    }

    const hoverGeneration = ++this.rasterHoverGeneration;
    const rasterHitPromise = this.getTopRasterHit(e);

    const dataTableLayerIds = Object.keys(this.dataTableLayersByGlId);
    const layerIds = this.existingLayerIds([
      ...dataTableLayerIds,
      ...this.interactiveVectorLayerIds,
    ]);
    // Deduplicate while preserving order (data-table ids first for lookup).
    const seen = new Set<string>();
    const uniqueLayerIds = layerIds.filter((id) => {
      if (seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });
    const features = uniqueLayerIds.length
      ? this.map!.queryRenderedFeatures(e.point, {
          layers: uniqueLayerIds,
        })
      : [];
    let topVectorIndex = -Infinity;
    if (features.length && uniqueLayerIds.indexOf(features[0].layer.id) > -1) {
      topVectorIndex = this.getLayerDrawIndex(features[0].layer.id);
    }

    const rasterHit = await rasterHitPromise;
    if (hoverGeneration !== this.rasterHoverGeneration) {
      return;
    }
    const rasterIndex = rasterHit?.layerIndex ?? -Infinity;

    if (rasterHit && rasterIndex > topVectorIndex && rasterIndex >= inatDrawIndex) {
      this.setHoveredFeature(undefined);
      if (this.applyRasterHoverUI(e, rasterHit.layer, rasterHit.hit)) {
        return;
      }
      // Unsupported raster type: fall through to vector/inat handling.
    }

    if (features.length && uniqueLayerIds.indexOf(features[0].layer.id) > -1) {
      const top = features[0];
      const dataTableConfig = this.dataTableLayersByGlId[top.layer.id];
      const dataTableTooltip = dataTableConfig
        ? this.buildDataTableValueTooltip(top, dataTableConfig, e)
        : undefined;
      const interactivitySetting = this.getInteractivitySettingForFeature(top);
      if (dataTableTooltip || interactivitySetting) {
        this.setHoveredFeature(top);
      } else {
        this.setHoveredFeature(undefined);
      }
      if (interactivitySetting) {
        let cursor = "";
        this.map!.getCanvas().style.cursor = cursor;
        let bannerMessages: string[] = [];
        let tooltip: Tooltip | undefined = undefined;
        let fixedBlocks: string[] = [];
        switch (interactivitySetting.type) {
          case InteractivityType.Banner:
            cursor = "default";
            bannerMessages = [
              Mustache.render(interactivitySetting.shortTemplate || "", {
                ...mustacheHelpers,
                ...(top.properties || {}),
              }),
            ];
            // Data-table value tooltip sits alongside the banner.
            tooltip = dataTableTooltip;
            break;
          case InteractivityType.Tooltip:
            cursor = "default";
            // Data-table value wins over an admin Tooltip template.
            tooltip =
              dataTableTooltip ??
              ({
                x: e.originalEvent.x,
                y: e.originalEvent.y,
                messages: [
                  Mustache.render(interactivitySetting.shortTemplate || "", {
                    ...mustacheHelpers,
                    ...(top.properties || {}),
                  }),
                ],
              } as Tooltip);
            break;
          case InteractivityType.Popup:
          case InteractivityType.AllPropertiesPopup:
          case InteractivityType.SidebarOverlay:
            cursor = "pointer";
            if (dataTableTooltip) {
              // Hover value inspection alongside click popup / sidebar.
              tooltip = dataTableTooltip;
            } else if (
              interactivitySetting.type === InteractivityType.SidebarOverlay &&
              interactivitySetting.shortTemplate?.length
            ) {
              tooltip = {
                x: e.originalEvent.x,
                y: e.originalEvent.y,
                messages: [
                  Mustache.render(interactivitySetting.shortTemplate, {
                    ...mustacheHelpers,
                    ...(top.properties || {}),
                  }),
                ],
              };
            }
            break;
          case InteractivityType.FixedBlock:
            cursor = "pointer";
            fixedBlocks = [
              Mustache.render(interactivitySetting.longTemplate || "", {
                ...mustacheHelpers,
                ...(top.properties || {}),
              }),
            ];
            tooltip = dataTableTooltip;
            break;
          default:
            tooltip = dataTableTooltip;
            break;
        }
        if (interactivitySetting.cursor !== "AUTO") {
          cursor = interactivitySetting.cursor.toString().toLowerCase();
        }
        this.map!.getCanvas().style.cursor = cursor;
        const currentInteractionTarget = `${top.id}-${interactivitySetting.id}`;
        // Skip redundant banner/popup updates for the same feature — but always
        // refresh when a data-table tooltip needs to follow the pointer.
        if (
          this.previousInteractionTarget === currentInteractionTarget &&
          !dataTableTooltip &&
          (interactivitySetting.type === InteractivityType.Banner ||
            interactivitySetting.type === InteractivityType.FixedBlock ||
            interactivitySetting.type === InteractivityType.Popup ||
            interactivitySetting.type === InteractivityType.AllPropertiesPopup)
        ) {
          // Don't waste cycles on a state update
        } else {
          this.previousInteractionTarget = currentInteractionTarget;
          this.updateUI({
            bannerMessages,
            tooltip,
            fixedBlocks,
          });
        }
      } else if (dataTableTooltip) {
        this.map!.getCanvas().style.cursor = "default";
        this.previousInteractionTarget = `${top.id}-data-table`;
        this.updateUI({
          bannerMessages: [],
          tooltip: dataTableTooltip,
          fixedBlocks: [],
        });
      } else {
        clear();
      }
    } else {
      this.setHoveredFeature(undefined);
      if (inatHit && inatDrawIndex >= topVectorIndex) {
        this.map!.getCanvas().style.cursor = "pointer";
        return;
      }
      clear();
    }
  };

  /**
   * Hover tooltip for a data-table proportional symbol, using `rawValue`
   * feature-state written by DataTableQueryManager.
   */
  private buildDataTableValueTooltip(
    feature: MapboxGeoJSONFeature,
    config: DataTableInteractiveLayer,
    e: MapMouseEvent
  ): Tooltip | undefined {
    if (feature.id === undefined || feature.id === null) {
      return undefined;
    }
    const state = this.map.getFeatureState({
      source: config.sourceId,
      id: feature.id,
      ...(config.sourceLayer ? { sourceLayer: config.sourceLayer } : {}),
    }) as {
      rawValue?: number | null;
      loading?: boolean;
      scaledValue?: number | null;
      valueClass?: string;
    };
    const position = {
      x: e.originalEvent.x,
      y: e.originalEvent.y,
    };
    const columnLabel = config.columnLabel || config.column;
    const series = config.getSeries?.(String(feature.id));
    const meta = {
      columnLabel,
      op: config.op,
      tableName: config.tableName,
      layerTitle: config.layerTitle,
      siteLabel: String(feature.id),
      series: series?.points,
      currentSteps: series?.currentSteps,
      rangeLabel: config.getRangeLabel?.(),
    };
    if (state.loading) {
      return {
        ...position,
        messages: ["Loading…"],
        dataTable: { ...meta, status: "loading" },
      };
    }
    if (
      !("rawValue" in state) &&
      !("scaledValue" in state) &&
      !("valueClass" in state)
    ) {
      // Feature-state not applied yet (style still settling).
      return undefined;
    }
    const rawValue = decodeDataTableRawValue(state);
    if (rawValue === null || rawValue === undefined) {
      return {
        ...position,
        messages: ["No data"],
        dataTable: { ...meta, status: "empty" },
      };
    }
    // Same formatter as the legend and "rows in calculation" modal so a
    // small nonzero mean such as 0.00019 is not rounded to "0".
    const formatted = formatLegendNumber(rawValue);
    return {
      ...position,
      messages: [`${columnLabel}: ${formatted}`],
      dataTable: {
        ...meta,
        status: "value",
        formattedValue: formatted,
      },
    };
  }

  private async getTopRasterHit(e: MapMouseEvent): Promise<{
    layer: InteractiveRasterLayer;
    hit: RasterPixelHit;
    layerIndex: number;
  } | null> {
    if (!this.interactiveRasterLayers.length) {
      return null;
    }
    const zoom = this.map.getZoom();
    const candidates = this.interactiveRasterLayers.filter((rasterLayer) => {
      const mapLayer = this.map.getLayer(rasterLayer.glLayerId);
      if (!mapLayer) return false;
      if (this.isClockHiddenLayer(rasterLayer.layer)) {
        return false;
      }
      if (this.isPaintHiddenOnMap(rasterLayer.glLayerId, mapLayer)) {
        return false;
      }
      return true;
    });
    const results = await Promise.all(
      candidates.map(async (rasterLayer) => {
        try {
          const hit = await queryRasterPixelValue(
            {
              sourceId: String(rasterLayer.source.id),
              url: rasterLayer.source.url || "",
              tileSize: rasterLayer.source.tileSize,
              minzoom: rasterLayer.source.minzoom,
              maxzoom: rasterLayer.source.maxzoom,
              rasterScale: rasterLayer.source.rasterScale,
              rasterOffset: rasterLayer.source.rasterOffset,
              respectScaleAndOffset: rasterLayer.respectScaleAndOffset,
              encoding: rasterLayer.encoding,
            },
            e.lngLat.lng,
            e.lngLat.lat,
            zoom
          );
          // Alpha 0 is already null. Also drop pixels the style paints fully
          // transparent (no-data / excluded classes) so they don't tooltip.
          if (
            !hit ||
            !rasterValueIsVisible(rasterLayer.paint, hit.encodedValue)
          ) {
            return null;
          }
          return {
            layer: rasterLayer,
            hit,
            layerIndex: this.getLayerDrawIndex(rasterLayer.glLayerId),
          };
        } catch {
          return null;
        }
      })
    );
    let best: {
      layer: InteractiveRasterLayer;
      hit: RasterPixelHit;
      layerIndex: number;
    } | null = null;
    for (const result of results) {
      if (result && (best === null || result.layerIndex > best.layerIndex)) {
        best = result;
      }
    }
    return best;
  }

  private rasterMustacheContext(
    hit: RasterPixelHit,
    rasterLayer: Pick<
      InteractiveRasterLayer,
      "legendLabels" | "labelFormat"
    >
  ) {
    return {
      ...mustacheHelpers,
      // Post scale/offset when s:respect-scale-and-offset is on.
      value: hit.value,
      // Categorical: s:legend-labels by encoded DN. Continuous: legend-style
      // formatting of the display value (locale, round-numbers, value-suffix).
      label: rasterInteractionLabel(
        rasterLayer.legendLabels,
        hit.encodedValue,
        hit.value,
        rasterLayer.labelFormat
      ),
    };
  }

  /** @returns true if raster UI was applied (caller should not fall through). */
  private applyRasterHoverUI(
    e: MapMouseEvent,
    rasterLayer: InteractiveRasterLayer,
    hit: RasterPixelHit
  ): boolean {
    const interactivitySetting = rasterLayer.layer.interactivitySettings;
    if (!interactivitySetting) {
      return false;
    }
    const ctx = this.rasterMustacheContext(hit, rasterLayer);
    // Prefer {{label}} so categorical legend names show by default; unlabeled
    // continuous values use the same formatting as legend min/max labels.
    const defaultTemplate = "{{label}}";
    const usesHoverText =
      interactivitySetting.type === InteractivityType.Banner ||
      interactivitySetting.type === InteractivityType.Tooltip;
    const renderedHoverText = usesHoverText
      ? Mustache.render(
          interactivitySetting.shortTemplate || defaultTemplate,
          ctx
        )
      : "";
    // No readable value (empty legend label, template that doesn't resolve).
    // Leave the map cursor alone and don't show an empty bubble.
    if (usesHoverText && isBlankHoverMessage(renderedHoverText)) {
      this.map!.getCanvas().style.cursor = "";
      delete this.previousInteractionTarget;
      this.updateUI({
        bannerMessages: [],
        tooltip: undefined,
        fixedBlocks: [],
      });
      return true;
    }
    let cursor = "";
    let bannerMessages: string[] = [];
    let tooltip: Tooltip | undefined = undefined;
    switch (interactivitySetting.type) {
      case InteractivityType.Banner:
        cursor = "default";
        bannerMessages = [renderedHoverText];
        break;
      case InteractivityType.Tooltip:
        cursor = "default";
        tooltip = {
          x: e.originalEvent.x,
          y: e.originalEvent.y,
          messages: [renderedHoverText],
        };
        break;
      case InteractivityType.Popup:
        cursor = "pointer";
        break;
      default:
        // Unsupported raster types (sidebar, all-properties, etc.)
        return false;
    }
    if (interactivitySetting.cursor !== "AUTO") {
      cursor = interactivitySetting.cursor.toString().toLowerCase();
    }
    this.map!.getCanvas().style.cursor = cursor;
    const target = `raster-${rasterLayer.layer.id}-${interactivitySetting.id}-${hit.value}`;
    if (
      this.previousInteractionTarget === target &&
      interactivitySetting.type !== InteractivityType.Tooltip
    ) {
      return true;
    }
    this.previousInteractionTarget = target;
    this.updateUI({
      bannerMessages,
      tooltip,
      fixedBlocks: [],
    });
    return true;
  }

  private async getTopInaturalistHit(e: MapMouseEvent) {
    if (!this.inaturalistConfigs.length) {
      return null;
    }
    let best: {
      hit: Awaited<ReturnType<typeof fetchInaturalistUtfgrid>>;
      layerIndex: number;
    } | null = null;

    for (const config of this.inaturalistConfigs) {
      const hit = await fetchInaturalistUtfgrid(
        this.map,
        config.params,
        e as any,
        config.layerLabel
      );
      if (!hit) {
        continue;
      }
      const layerIndex = this.getInatLayerDrawIndex(config.sourceId);
      if (best === null || layerIndex > best.layerIndex) {
        best = { hit, layerIndex };
      }
    }

    return best;
  }

  private getInatLayerDrawIndex(sourceId: string) {
    const layers = this.map.getStyle().layers || [];
    // eslint-disable-next-line i18next/no-literal-string
    const candidateSources = [
      sourceId,
      `${sourceId}-points`,
      `${sourceId}-heatmap`,
      `${sourceId}-grid`,
    ];
    return layers.reduce((max, l, idx) => {
      const src: string | undefined =
        // @ts-ignore
        "source" in l ? (l as any).source?.toString() : undefined;
      if (src && candidateSources.includes(src)) {
        return Math.max(max, idx);
      }
      return max;
    }, -Infinity);
  }

  getInteractivitySettingForFeature(feature: MapboxGeoJSONFeature) {
    if (isSeaSketchLayerId(feature.layer.id)) {
      const dataLayer = this.layers[layerIdFromStyleLayerId(feature.layer.id)];
      if (!dataLayer) {
        return;
      }
      return dataLayer.interactivitySettings;
    } else if (this.layers[feature.layer.id]) {
      const dataLayer = this.layers[feature.layer.id];
      if (!dataLayer) {
        return;
      }
      return dataLayer.interactivitySettings;
    } else {
      if (
        this.basemap &&
        this.basemap.interactivitySettings?.layers &&
        this.basemap.interactivitySettings?.layers.indexOf(feature.layer.id) !==
          -1
      ) {
        return this.basemap.interactivitySettings;
      } else {
      }
    }
  }

  private getLayerDrawIndex(layerId: string) {
    const layers = this.map.getStyle().layers || [];
    return layers.findIndex((l) => l.id === layerId);
  }
}

const mustacheHelpers = {
  round: () => (text: string, render: (str: string) => string) => {
    return `${Math.round(parseFloat(render(text)))}`;
  },
  round1Digit: () => (text: string, render: (str: string) => string) => {
    return `${Math.round(parseFloat(render(text)) * 10) / 10}`;
  },
  round2Digits: () => (text: string, render: (str: string) => string) => {
    return `${Math.round(parseFloat(render(text)) * 100) / 100}`;
  },
};

function sortFeaturesByLayer(
  features: mapboxgl.MapboxGeoJSONFeature[],
  style: mapboxgl.Style
) {
  // sort features by their index in the style's layer list
  return features.sort((a, b) => {
    const aLayerIndex = style.layers.findIndex(
      (layer) => layer.id === a.layer.id
    );
    const bLayerIndex = style.layers.findIndex(
      (layer) => layer.id === b.layer.id
    );
    return bLayerIndex - aLayerIndex;
  });
}

function sameStringSet(a: string[], b: string[]) {
  if (a.length !== b.length) {
    return false;
  }
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
}
